import { Formik, Field, Form } from 'formik';
import React, { useEffect, useState } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import TextArea from 'components/atoms/textarea';
import Button from 'components/atoms/button';
import DraggableModal from 'components/organisms/draggableModal';
import { showResult } from 'infrastructure/hooks/utils';
import { useTracker } from 'infrastructure/hooks/shared/useTracker';
import { getTrackerTitle } from 'infrastructure/hooks/shared/useTracker/getTrackerTitle';

import s from './styles.module.scss';

import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';
import type { ISmsTemplate } from 'infrastructure/interfaces/i-sms-template';

export interface SmsModalProps {
  isOpen: boolean;
  toggle: () => void;
  patientGuid: string;
  smsTemplates: ISmsTemplate[];
  loading: boolean;
  submit: (data: ISendSmsDataProps) => Promise<{ error: null | string }>;
  isRpmPanel: boolean;
  isPrimaryNumber: boolean;
}

const SmsModal: React.FC<SmsModalProps> = ({
  isOpen,
  toggle,
  patientGuid,
  smsTemplates,
  loading,
  submit,
  isRpmPanel,
  isPrimaryNumber,
}) => {
  const primaryTitle = 'Send SMS Primary Contact';
  const secondaryTitle = 'Send SMS Secondary Contact';
  const minimizedTitle = 'SMS';
  const title = isPrimaryNumber ? primaryTitle : secondaryTitle;

  const [isMinimized, setIsMinimized] = useState(false);
  const [topic, setTopic] = useState('');
  const {
    time,
    secondsRef,
    start,
    stop,
    createTrackerLoading,
    trackerGuid,
    deleteTracker,
  } = useTracker();

  useEffect(() => {
    if (isRpmPanel && isOpen) {
      start();
    }
  }, [isRpmPanel, isOpen]);

  const handleSubmit = async (values: { message: string }) => {
    stop();
    const postData: ISendSmsDataProps = {
      patientGuid,
      topic,
      type: 'sms',
      message: values.message,
      isRpmPanel,
      isPrimaryNumber,
      timerDuration: isRpmPanel ? secondsRef.current : 0,
      trackerGuid: isRpmPanel ? trackerGuid : null,
    };
    const { error } = await submit(postData);
    if (!error) {
      showResult();
      toggle();
      return;
    }
    if (isRpmPanel && isOpen) {
      start();
    }
  };

  const onTemplateChange = (
    templateGuid: string,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    const smsTemplate = smsTemplates.find(
      (template) => templateGuid === template.id,
    );
    setTopic(smsTemplate?.topic ?? '');
    setFieldValue('message', smsTemplate?.text ?? '');
  };

  const rightTitle = getTrackerTitle({
    isMinimized,
    trackerTimer: time,
  });

  return (
    <>
      {isOpen && (
        <DraggableModal
          title={isMinimized ? minimizedTitle : title}
          rightTitle={isRpmPanel ? rightTitle : undefined}
          isOpen={isOpen}
          toggle={() => {
            toggle();
            deleteTracker();
          }}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
        >
          <Formik
            initialValues={{
              message: '',
            }}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, values }) => (
              <Form>
                <div className="sms-popup-content">
                  {isRpmPanel && (
                    <FormGroup tag="fieldset">
                      {smsTemplates.map((template) => (
                        <FormGroup check key={template.id}>
                          <Label check>
                            <Input
                              type="radio"
                              name="template_id"
                              value={template.id}
                              onChange={() =>
                                onTemplateChange(template.id, setFieldValue)
                              }
                            />
                            {template.topic}
                          </Label>
                        </FormGroup>
                      ))}
                    </FormGroup>
                  )}
                  <div>
                    <Field
                      className="sms-textarea"
                      id="message"
                      name="message"
                      type="message"
                      as={TextArea}
                      rows="8"
                      placeholder={
                        isRpmPanel ? 'Type or select option on the left' : ''
                      }
                    />
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    type="submit"
                    text="Send"
                    data-cy="send-button"
                    disabled={
                      loading ||
                      createTrackerLoading ||
                      values.message === '' ||
                      values.message === undefined
                    }
                  />
                </div>
              </Form>
            )}
          </Formik>
        </DraggableModal>
      )}
    </>
  );
};

export default SmsModal;
